import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import type { SubscriptionNotificationSettings } from '../../api/client';
import { C } from '../ChatAssistant/compact/tokens';
import { font as f } from '../ChatAssistant/compact/style';
import { GhostBtn, Modal, ModalHead, Note, PrimaryBtn, TextBtn } from './ui';
import { ROW_LINE } from './styles';
import { BusyLabel } from '../Spinner';

const DAYS = [1, 3, 7] as const;

export function NotificationsDialog({ initial, onClose, onSaved }: { initial: SubscriptionNotificationSettings | null; onClose: () => void; onSaved: (s: SubscriptionNotificationSettings) => void }) {
  const { t } = useTranslation('paymentsCompact');
  const [chatId, setChatId] = useState(initial?.subscription_chat_id || '');
  const [days, setDays] = useState<number[]>(initial?.advance_days || []);
  const [test, setTest] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const trimmed = chatId.trim();
  const valid = /^-?\d{6,}$/.test(trimmed);

  const toggleDay = (d: number) => setDays((p) => (p.includes(d) ? p.filter((x) => x !== d) : [...p, d].sort((a, b) => a - b)));

  const sendTest = async () => {
    if (!valid || test === 'sending') return;
    setTest('sending');
    setErr('');
    try {
      await api.testSubscriptionTelegram(trimmed);
      setTest('sent');
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('errors.test'));
      setTest('idle');
    }
  };

  const save = async () => {
    if (trimmed && !valid) return;
    setSaving(true);
    setErr('');
    try {
      const res = await api.updateNotificationSettings({ subscription_chat_id: trimmed || null, advance_days: days });
      onSaved(res);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('errors.saveSettings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal width={440} onClose={onClose}>
      <ModalHead icon="send" title={t('ntf.title')} onClose={onClose} />
      <span style={{ padding: '0 16px 12px', font: f(400, 12), color: C.sub }}>{t('ntf.desc')}</span>
      <div style={{ margin: '0 16px', border: `1px solid ${C.border}`, borderRadius: 8, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px' }}>
          <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span style={{ font: f(500, 13), color: C.text }}>{t('ntf.groupId')}</span>
            <span style={{ font: f(400, 11.5), color: C.muted }}>{t('ntf.groupIdHint')}</span>
          </span>
          <input
            value={chatId}
            onChange={(e) => { setChatId(e.target.value); setTest('idle'); }}
            spellCheck={false}
            placeholder="-100…"
            style={{ width: 170, height: 30, boxSizing: 'border-box', padding: '0 9px', background: C.input, border: `1px solid ${trimmed && !valid ? C.purplePale : C.borderHi}`, borderRadius: 6, color: C.text, font: '400 12.5px var(--font-mono)', outline: 'none' }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderTop: ROW_LINE }}>
          <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span style={{ font: f(500, 13), color: C.text }}>{t('ntf.remind')}</span>
            <span style={{ font: f(400, 11.5), color: C.muted }}>{t('ntf.remindHint')}</span>
          </span>
          {/* Several reminders can be on at once, so each segment toggles on its own. */}
          <div style={{ display: 'flex', gap: 2, padding: 2, background: C.input, border: `1px solid ${C.border}`, borderRadius: 7 }}>
            {DAYS.map((d) => {
              const on = days.includes(d);
              return (
                <button key={d} type="button" aria-pressed={on} onClick={() => toggleDay(d)} style={{ height: 24, padding: '0 9px', border: 0, borderRadius: 5, background: on ? C.segActive : 'transparent', color: on ? C.text : C.sub, font: f(500, 12), cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {t('ntf.days', { count: d })}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {err && <Note icon="alert-circle" tone="accent" style={{ padding: '10px 16px 0' }}>{err}</Note>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px' }}>
        <GhostBtn icon={test === 'sent' ? 'check' : 'send'} onClick={() => void sendTest()} disabled={!valid || test === 'sending'}>
          <BusyLabel busy={test === 'sending'} label={t('ntf.sending')}>{test === 'sent' ? t('ntf.testSent') : t('ntf.sendTest')}</BusyLabel>
        </GhostBtn>
        <span style={{ flex: 1 }} />
        <TextBtn onClick={onClose}>{t('cancel')}</TextBtn>
        <PrimaryBtn height={30} onClick={() => void save()} disabled={saving || (!!trimmed && !valid)} style={{ font: f(600, 12.5) }}>
          <BusyLabel busy={saving} label={t('saving')}>{t('save')}</BusyLabel>
        </PrimaryBtn>
      </div>
    </Modal>
  );
}

